import { useAuth0 } from "@auth0/auth0-react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";

import { Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { AvailabilityTypeEnum, useCareResourceByEmailQuery } from "@api/graphql/types-and-hooks";

export const OnSchedResourcePill: React.FC = () => {
  const { user } = useAuth0();
  const userEmail = user?.email;
  const [isExpanded, setIsExpanded] = useState(false);

  // Self-contained query - fetch resource details
  const { data, isLoading, error } = useCareResourceByEmailQuery(
    { email: userEmail! },
    {
      enabled: !!userEmail,
      retry: false,
    },
  );

  const resource = data?.careResourceByEmail;

  // Don't render if no data or loading
  if (isLoading || !resource) {
    return null;
  }

  if (error) {
    return null;
  }

  const getAvailabilityTypeDisplay = (type: AvailabilityTypeEnum) => {
    if (type === AvailabilityTypeEnum.Allocation) return "Allocation Blocks (default busy, explicit avaiablility)";
    if (type === AvailabilityTypeEnum.Schedule) return "Weekly Availability (default available, explicit busy)";
    return type || "Unknown";
  };

  const getAvailabilityTypeBadgeColor = (type: AvailabilityTypeEnum) => {
    if (type === AvailabilityTypeEnum.Schedule) return "bg-globemallow-200 text-globemallow-800";
    if (type === AvailabilityTypeEnum.Allocation) return "bg-hatch-200 text-hatch-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="inline-block relative">
      {/* Main Pill - Clickable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-3 px-4 py-2 rounded-full shadow-xs border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        style={{
          background: "linear-gradient(135deg, #b7ff33 0%, #dbff99 100%)",
        }}
      >
        {/* OnSched Logo */}
        <div className="flex items-center flex-row gap-2">
          <img src="https://app-stage.onsched.com/logo.png" alt="OnSched" className="h-7 w-7 object-contain" />
          <Small className="font-semibold text-gray-900 leading-tight">OnSched Resource:</Small>
        </div>

        {/* Resource Details */}
        <div className="flex flex-col text-left">
          <Small className="font-semibold text-gray-900 leading-tight">{resource.name}</Small>
          <Small className="text-gray-700 leading-tight">{resource.email}</Small>
        </div>

        {/* Connected Badge */}
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-600 animate-pulse" />
          <Small className="font-medium text-gray-900">Linked</Small>
        </div>

        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-700" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-700" />
        )}
      </button>

      {/* Expanded Details Panel */}
      <div
        className={`absolute left-0 top-full mt-3 z-50 overflow-hidden transition-all duration-300 ease-in-out w-full ${
          isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-4">
          {/* Availability Type */}
          <div>
            <Small className="text-gray-600 font-medium block mb-1">Availability Mode</Small>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getAvailabilityTypeBadgeColor(resource.availabilityType as AvailabilityTypeEnum)}`}
            >
              {getAvailabilityTypeDisplay(resource.availabilityType as AvailabilityTypeEnum)}
            </span>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            {resource.timezone && (
              <div>
                <Small className="text-gray-600 font-medium block">Timezone</Small>
                <Paragraph className="text-sm">{resource.timezone}</Paragraph>
              </div>
            )}
            {resource.phone && (
              <div>
                <Small className="text-gray-600 font-medium block">Phone</Small>
                <Paragraph className="text-sm">{resource.phone}</Paragraph>
              </div>
            )}
            {resource.bookingLimit !== null && resource.bookingLimit !== undefined && (
              <div>
                <Small className="text-gray-600 font-medium block">Booking Limit</Small>
                <Paragraph className="text-sm">{resource.bookingLimit}</Paragraph>
              </div>
            )}
            {resource.bookingsPerSlot !== null && resource.bookingsPerSlot !== undefined && (
              <div>
                <Small className="text-gray-600 font-medium block">Bookings Per Slot</Small>
                <Paragraph className="text-sm">{resource.bookingsPerSlot}</Paragraph>
              </div>
            )}
          </div>

          {/* About */}
          {resource.about && (
            <div>
              <Small className="text-gray-600 font-medium block mb-1">About</Small>
              <Paragraph className="text-sm text-gray-700">{resource.about}</Paragraph>
            </div>
          )}

          {/* Associated Services */}
          {resource.services && resource.services.length > 0 && (
            <div>
              <Small className="text-gray-600 font-medium block mb-2">
                Associated Services ({resource.services.length})
              </Small>
              <div className="space-y-2">
                {resource.services
                  .filter((service) => !!service)
                  .map((service) => (
                    <div key={service!.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <Paragraph className="font-medium text-sm">{service!.name}</Paragraph>
                          {service!.description && (
                            <Small className="text-gray-600 block mt-1">{service!.description}</Small>
                          )}
                          <div className="flex gap-3 mt-2">
                            <Small className="text-gray-600">
                              <span className="font-medium">Duration:</span> {service!.durationMinutes} min
                            </Small>
                            {service!.priceDollars && (
                              <Small className="text-gray-600">
                                <span className="font-medium">Price:</span> ${service!.priceDollars}
                              </Small>
                            )}
                          </div>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            service!.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {service!.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Associated Locations */}
          {resource.locations && resource.locations.length > 0 && (
            <div>
              <Small className="text-gray-600 font-medium block mb-2">
                Associated Locations ({resource.locations.length})
              </Small>
              <div className="space-y-2">
                {resource.locations
                  .filter((location) => !!location)
                  .map((location) => (
                    <div key={location!.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <Paragraph className="font-medium text-sm">{location!.name}</Paragraph>
                      {location!.address && <Small className="text-gray-600 block mt-1">{location!.address}</Small>}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* OnSched Resource ID */}
          <div className="pt-3 border-t border-gray-200">
            <Small className="text-gray-500">
              <span className="font-medium">OnSched ID:</span> {resource.id}
            </Small>
          </div>
        </div>
      </div>
    </div>
  );
};
